// API Protection → Management Overview — native (non-Superset) charts. Mirrors
// the Dashboard overview (`dashboard-charts.ts`) but for Superset board 58, which
// adds a user-selectable Time Range (the dashboard's "time_range" template filter):
//   GET /api-protection/overview/charts?tz=&range= → { charts: WaapChart[] }
import { useQuery } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, waapPath, WAAP_AREAS } from './_shared';
import type { WaapChartsResponse } from './dashboard-charts';

/** Time Range options — the exact strings the vendor datasets match on via
 *  filter_values('time_range'); do NOT relabel (they ARE the filter value). */
export const API_PROTECTION_TIME_RANGES = [
  'Last Hour',
  'Last 24 Hours',
  'Last 7 Days',
  'Last 30 Days',
  'This Month',
  'Last Month',
] as const;

export type ApiProtectionTimeRange = (typeof API_PROTECTION_TIME_RANGES)[number];

/** Read the normalized API Protection overview widgets for a website's account.
 *  `range` drives the dashboard's Time Range filter. Returns null when the board
 *  is not configured server-side (400/404/501) so the page can show a clear state. */
export function useWaapApiProtectionOverviewCharts(
  domainId: string | undefined,
  range: ApiProtectionTimeRange,
) {
  return useQuery({
    queryKey: [...waapKeys.aspect(domainId ?? '', WAAP_AREAS.apiProtectionOverviewCharts), range],
    enabled: !!domainId,
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<WaapChartsResponse | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const path = waapPath(domainId as string, WAAP_AREAS.apiProtectionOverviewCharts);
        const res = await serviceApiV3.get<WaapChartsResponse>(
          `${path}?tz=${encodeURIComponent(tz)}&range=${encodeURIComponent(range)}`,
        );
        return res.data;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404 || status === 501) return null;
        throw e;
      }
    },
  });
}
